import { Navbar, Footer } from "@/components/Layout";
import { useListingFlow, ListingType } from "@/contexts/ListingFlowContext";
import { useNavigate } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AddressAutocompleteInput } from "@/components/AddressAutocompleteInput";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";

const unitLabels: Record<ListingType, string> = {
  trad: "kr/träd",
  plankor: "kr/planka",
  ved: "kr/kubik",
};

export default function CreateListingStep4() {
  const { data, updateData, resetData } = useListingFlow();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!data.category) {
    navigate("/skapa-annons", { replace: true });
    return null;
  }

  const price = parseFloat(data.price) || 0;
  const quantity = parseInt(data.quantity) || 0;
  const total = price * quantity;
  const unit = unitLabels[data.listingType ?? "trad"];

  const deliverySelected = data.pickupEnabled || data.shippingEnabled;
  const pickupAddressMissing = data.pickupEnabled && !data.pickupAddress.trim();

  const handlePublish = async () => {
    if (!user || !deliverySelected || pickupAddressMissing) return;
    setLoading(true);

    const deliveryValue = data.pickupEnabled && data.shippingEnabled
      ? "båda"
      : data.shippingEnabled
      ? "leverans"
      : "hämta";

    const { error } = await supabase.from("listings").insert({
      seller_id: user.id,
      title: data.title,
      description: data.description,
      category: data.category,
      sub_category: data.subCategory || null,
      species: data.species,
      price,
      price_unit: unit,
      location: data.municipality,
      delivery: deliveryValue,
      images: data.images,
      listing_type: data.listingType,
      province: data.province || null,
      municipality: data.municipality,
      wood_age: data.woodAge ? parseInt(data.woodAge) : null,
      diameter: data.diameter || null,
      moisture_content: data.moistureContent || null,
      processing_grade: data.processingGrade || null,
      quantity: quantity || null,
      pickup_enabled: data.pickupEnabled,
      shipping_enabled: data.shippingEnabled,
      shipping_price: data.shippingPrice ? parseFloat(data.shippingPrice) : null,
      delivery_radius_km: data.deliveryRadiusKm ? parseFloat(data.deliveryRadiusKm) : null,
      pickup_address: data.pickupEnabled ? data.pickupAddress.trim() : null,
      plank_length_cm: data.listingType === "plankor" && data.plankLength ? parseFloat(data.plankLength) : null,
      plank_width_cm: data.listingType === "plankor" && data.plankWidth ? parseFloat(data.plankWidth) : null,
      plank_height_cm: data.listingType === "plankor" && data.plankHeight ? parseFloat(data.plankHeight) : null,
    } as any);

    setLoading(false);
    if (error) {
      toast.error("Kunde inte publicera: " + error.message);
    } else {
      toast.success("Annons publicerad!");
      resetData();
      navigate("/dashboard");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="container max-w-lg flex-1 py-6">
        <BackButton fallback="/skapa-annons/platsinfo" />

        <h1 className="mb-6 font-display text-2xl font-bold text-foreground">Pris, kvantitet & leverans</h1>

        <div className="mb-4">
          <Label>Pris ({unit})</Label>
          <Input
            type="number"
            value={data.price}
            onChange={(e) => updateData({ price: e.target.value })}
            placeholder="0"
          />
        </div>

        <div className="mb-6">
          <Label>Antal</Label>
          <Input
            type="number"
            value={data.quantity}
            onChange={(e) => updateData({ quantity: e.target.value })}
            placeholder="0"
          />
        </div>

        {/* Total display */}
        <div className="mb-8 rounded-xl border border-border bg-card p-4 text-center">
          <p className="text-sm text-muted-foreground">Totalt värde</p>
          <p className="text-3xl font-bold text-foreground">
            {total.toLocaleString("sv-SE")} kr
          </p>
          {price > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              {price.toLocaleString("sv-SE")} {unit}
            </p>
          )}
        </div>

        {/* Delivery section */}
        <h2 className="mb-4 font-display text-lg font-bold text-foreground">Leverans</h2>

        <div className="mb-4 space-y-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Upphämtning</p>
                <p className="text-xs text-muted-foreground">Köparen hämtar hos dig</p>
              </div>
              <Switch
                checked={data.pickupEnabled}
                onCheckedChange={(v) => updateData({ pickupEnabled: v })}
              />
            </div>

            {data.pickupEnabled && (
              <div className="mt-4 space-y-2 border-t border-border pt-4">
                <Label htmlFor="pickup-address">Upphämtningsadress</Label>
                <AddressAutocompleteInput
                  id="pickup-address"
                  value={data.pickupAddress}
                  onChange={(v) => updateData({ pickupAddress: v })}
                  placeholder="Ange adressen där virket hämtas"
                />
                {pickupAddressMissing && (
                  <p className="text-sm text-destructive">Ange en upphämtningsadress.</p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Frakt</p>
                <p className="text-xs text-muted-foreground">Du levererar till köparen</p>
              </div>
              <Switch
                checked={data.shippingEnabled}
                onCheckedChange={(v) => updateData({ shippingEnabled: v })}
              />
            </div>

            {data.shippingEnabled && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <div>
                  <Label>Fraktpris (kr)</Label>
                  <Input
                    type="number"
                    value={data.shippingPrice}
                    onChange={(e) => updateData({ shippingPrice: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Leveransradius (km)</Label>
                  <Input
                    type="number"
                    value={data.deliveryRadiusKm}
                    onChange={(e) => updateData({ deliveryRadiusKm: e.target.value })}
                    placeholder="T.ex. 50"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {!deliverySelected && (
          <p className="mb-4 text-sm text-destructive">Minst ett leveransalternativ måste vara valt.</p>
        )}

        <Button
          className="w-full"
          disabled={loading || price <= 0 || quantity <= 0 || !deliverySelected || pickupAddressMissing}
          onClick={handlePublish}
        >
          {loading ? "Publicerar…" : "Publicera"}
        </Button>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}
